import { z } from 'zod/v4';

export const UpdateQuestionnaireRequestsDueDateParamsSchema = z.object({
  id: z.string().min(1),
});

export type UpdateQuestionnaireRequestsDueDateParams = z.infer<
  typeof UpdateQuestionnaireRequestsDueDateParamsSchema
>;

/**
 * Body of `PATCH /api/questionnaires/:id/requests/due-date`, ported from the
 * legacy `bulkUpdateDueDate.body` JSON Schema.
 *
 * `questionnaireId` moved out of the body and into the **path**, matching the
 * sibling create, delete and reminder endpoints. All four now agree on where a
 * request collection lives, and the id can no longer disagree with the resource
 * being addressed.
 *
 * `requestIds` is `.min(1)` — the legacy schema declared `minItems: 1`, the
 * opposite of the reminder's `minItems: 0`. There is no "apply to every request"
 * branch here: the legacy service used the submitted ids verbatim, so an empty
 * array would have updated nothing while still answering 2xx. Do not harmonise
 * this with `RemindQuestionnaireRequestsBodySchema`.
 *
 * Duplicates are rejected rather than tolerated: the service compares found rows
 * against the raw submitted count, so `["a","a"]` matches one row against two ids
 * and 404s — as it does today. Same call as the delete and reminder paths;
 * de-duplicating would silently widen what the endpoint accepts, and no frontend
 * can produce duplicates (each builds the array from a selection Set or a single
 * row id).
 *
 * `dueDate` is **required and non-nullable**, which is stricter than it looks:
 * every caller passes `?? null` when its date input is empty, so it is tempting
 * to read that as "null clears the due date". It does not, and never did —
 * `{type: 'string', format: 'date'}` rejects null, so the legacy endpoint 400s on
 * it. Clearing is not a product action either: the old frontend's
 * `DueDateAction` disables Save with `isDisabled={!dueDate}`, and frontend-v2's
 * `DateEditor` commits on pick with no clear affordance. Accepting null here
 * would invent a capability the UI has no way to express, and would publish a
 * "due date changed" email announcing a date of `null`.
 */
export const UpdateQuestionnaireRequestsDueDateBodySchema = z.object({
  requestIds: z.array(z.string().min(1)).min(1),
  dueDate: z.iso.date(),
});

export type UpdateQuestionnaireRequestsDueDateBody = z.infer<
  typeof UpdateQuestionnaireRequestsDueDateBodySchema
>;
